import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Loader2, Receipt, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API } from "@/utils/constants";

const fmt = (v, cur = "USD") => new Intl.NumberFormat("en-US", { style: "currency", currency: cur || "USD" }).format(v || 0);
const CATEGORIES = ["software", "travel", "supplies", "marketing", "contractors", "rent", "other"];
const CAT_COLORS = { software: "bg-sky-500/15 text-sky-600", travel: "bg-amber-500/15 text-amber-600", supplies: "bg-emerald-500/15 text-emerald-600", marketing: "bg-violet-500/15 text-violet-600", contractors: "bg-pink-500/15 text-pink-600", rent: "bg-slate-500/15 text-slate-600", other: "bg-muted text-muted-foreground" };

export const CRMExpenses = ({ refreshKey }) => {
  const [expenses, setExpenses] = useState(null);
  const [editor, setEditor] = useState(null);
  const [viewReceipt, setViewReceipt] = useState(null);

  const onReceiptFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 500 * 1024) { toast.error("Receipt too large — keep it under 500KB"); e.target.value = ""; return; }
    const reader = new FileReader();
    reader.onload = () => setEditor((ed) => ({ ...ed, receipt: reader.result, receipt_name: f.name }));
    reader.readAsDataURL(f);
  };

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/crm/expenses`); setExpenses(r.data); }
    catch { setExpenses([]); toast.error("Failed to load expenses"); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const save = async (e) => {
    e.preventDefault();
    const payload = { description: editor.description, vendor: editor.vendor || "", category: editor.category || "other", amount: parseFloat(editor.amount) || 0, currency: editor.currency || "USD", date: editor.date || null, receipt: editor.receipt || "", receipt_name: editor.receipt_name || "" };
    try {
      if (editor.id) await axios.put(`${API}/crm/expenses/${editor.id}`, payload);
      else await axios.post(`${API}/crm/expenses`, payload);
      setEditor(null); load(); toast.success("Expense saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save expense"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try { await axios.delete(`${API}/crm/expenses/${id}`); load(); }
    catch { toast.error("Failed to delete"); }
  };

  if (expenses === null) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const total = expenses.reduce((s, x) => s + (x.amount || 0), 0);

  return (
    <div data-testid="crm-expenses">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-muted-foreground">{expenses.length} expense{expenses.length === 1 ? "" : "s"} · Total {fmt(total)}</p>
        <Button size="sm" onClick={() => setEditor({ description: "", vendor: "", category: "other", amount: "", currency: "USD", date: new Date().toISOString().slice(0, 10) })} data-testid="add-expense-btn"><Plus className="h-4 w-4 mr-1" />Expense</Button>
      </div>

      {expenses.length === 0 ? (
        <div className="text-center py-16">
          <Receipt className="h-8 w-8 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No expenses yet — track them here to see real profit on your dashboard.</p>
        </div>
      ) : (
        <div className="border rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50"><tr><th className="text-left p-3 font-medium">Description</th><th className="text-left p-3 font-medium hidden sm:table-cell">Vendor</th><th className="text-left p-3 font-medium">Category</th><th className="text-left p-3 font-medium hidden md:table-cell">Date</th><th className="text-right p-3 font-medium">Amount</th><th className="p-3 w-20"></th></tr></thead>
            <tbody>
              {expenses.map((x) => (
                <tr key={x.id} className="border-t hover:bg-muted/30" data-testid={`expense-row-${x.id}`}>
                  <td className="p-3 font-medium">
                    {x.description}
                    {x.receipt && <button type="button" onClick={() => setViewReceipt(x)} className="ml-1.5 p-0.5 rounded hover:bg-muted text-muted-foreground align-middle" title={`Receipt: ${x.receipt_name || "attached"}`} data-testid={`view-receipt-${x.id}`}><Paperclip className="h-3.5 w-3.5 inline" /></button>}
                  </td>
                  <td className="p-3 text-muted-foreground hidden sm:table-cell">{x.vendor || "—"}</td>
                  <td className="p-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${CAT_COLORS[x.category] || CAT_COLORS.other}`}>{x.category}</span></td>
                  <td className="p-3 text-muted-foreground hidden md:table-cell">{x.date}</td>
                  <td className="p-3 text-right font-semibold text-red-500">−{fmt(x.amount, x.currency)}</td>
                  <td className="p-3">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => setEditor({ ...x })} className="p-1.5 rounded hover:bg-muted text-muted-foreground" data-testid={`edit-expense-${x.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => del(x.id)} className="p-1.5 rounded hover:bg-red-500/10 text-muted-foreground hover:text-red-500" data-testid={`delete-expense-${x.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Sheet open={!!editor} onOpenChange={(v) => !v && setEditor(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="expense-drawer">
          <SheetHeader><SheetTitle>{editor?.id ? "Edit Expense" : "New Expense"}</SheetTitle></SheetHeader>
          {editor && (
            <form onSubmit={save} className="space-y-4 mt-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Description</label>
                <Input required placeholder="What did you pay for?" value={editor.description} onChange={(e) => setEditor({ ...editor, description: e.target.value })} data-testid="expense-description" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Vendor</label>
                  <Input placeholder="Vendor" value={editor.vendor} onChange={(e) => setEditor({ ...editor, vendor: e.target.value })} data-testid="expense-vendor" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Category</label>
                  <Select value={editor.category} onValueChange={(v) => setEditor({ ...editor, category: v })}>
                    <SelectTrigger data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Amount</label>
                  <Input required type="number" min="0" step="0.01" placeholder="0.00" value={editor.amount} onChange={(e) => setEditor({ ...editor, amount: e.target.value })} data-testid="expense-amount" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Currency</label>
                  <Select value={editor.currency || "USD"} onValueChange={(v) => setEditor({ ...editor, currency: v })}>
                    <SelectTrigger data-testid="expense-currency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{["USD", "EUR", "GBP"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Date</label>
                  <Input type="date" value={editor.date || ""} onChange={(e) => setEditor({ ...editor, date: e.target.value })} data-testid="expense-date" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Receipt <span className="font-normal">(image or PDF, max 500KB)</span></label>
                {editor.receipt ? (
                  <div className="flex items-center gap-2 text-xs bg-muted/50 border rounded-lg px-3 py-2" data-testid="expense-receipt-attached">
                    <Paperclip className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="truncate flex-1">{editor.receipt_name || "Receipt attached"}</span>
                    <button type="button" onClick={() => setEditor({ ...editor, receipt: "", receipt_name: "" })} className="text-muted-foreground hover:text-red-500" data-testid="remove-receipt-btn"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ) : (
                  <Input type="file" accept="image/*,application/pdf" onChange={onReceiptFile} className="text-xs" data-testid="expense-receipt-input" />
                )}
              </div>
              <Button type="submit" className="w-full" data-testid="save-expense-btn">Save Expense</Button>
            </form>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={!!viewReceipt} onOpenChange={(v) => !v && setViewReceipt(null)}>
        <DialogContent className="max-w-2xl" data-testid="receipt-dialog">
          <DialogHeader><DialogTitle className="text-sm">{viewReceipt?.receipt_name || "Receipt"} — {viewReceipt?.description}</DialogTitle></DialogHeader>
          {viewReceipt?.receipt?.startsWith("data:image") ? (
            <img src={viewReceipt.receipt} alt="receipt" className="max-h-[70vh] w-auto mx-auto rounded-lg border" />
          ) : (
            <div className="text-center py-6">
              <Paperclip className="h-6 w-6 text-muted-foreground/50 mx-auto mb-2" />
              <a href={viewReceipt?.receipt} download={viewReceipt?.receipt_name || "receipt.pdf"} className="text-sm text-primary underline" data-testid="download-receipt-link">Download {viewReceipt?.receipt_name || "receipt"}</a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
